// src/pages/Login.jsx
import React, { useState, useContext, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Stack,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  alpha,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  ArrowForward,
  Person,
  Lock,
  TrendingUp,
  ShowChart,
  CandlestickChart,
} from '@mui/icons-material';
import AuthContext from '../contexts/AuthContext';

// ==================== ANIMATED BACKGROUND ====================

const GridBackground = () => (
  <Box
    sx={{
      position: 'absolute',
      inset: 0,
      overflow: 'hidden',
      pointerEvents: 'none',
      zIndex: 0,
    }}
  >
    {/* Base gradient */}
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        background: 'linear-gradient(160deg, #04070b 0%, #0a1019 40%, #060d16 70%, #020408 100%)',
      }}
    />

    {/* Animated grid lines */}
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        backgroundImage: `
          linear-gradient(${alpha('#00d4ff', 0.03)} 1px, transparent 1px),
          linear-gradient(90deg, ${alpha('#00d4ff', 0.03)} 1px, transparent 1px)
        `,
        backgroundSize: '60px 60px',
        animation: 'gridMove 20s linear infinite',
        '@keyframes gridMove': {
          '0%': { transform: 'translate(0, 0)' },
          '100%': { transform: 'translate(60px, 60px)' },
        },
      }}
    />

    {/* Glow orbs */}
    <Box
      sx={{
        position: 'absolute',
        top: '15%',
        left: '20%',
        width: 500,
        height: 500,
        borderRadius: '50%',
        background: `radial-gradient(circle, ${alpha('#00d4ff', 0.06)} 0%, transparent 70%)`,
        filter: 'blur(60px)',
        animation: 'orbFloat1 12s ease-in-out infinite',
        '@keyframes orbFloat1': {
          '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
          '33%': { transform: 'translate(30px, -20px) scale(1.05)' },
          '66%': { transform: 'translate(-20px, 15px) scale(0.95)' },
        },
      }}
    />
    <Box
      sx={{
        position: 'absolute',
        bottom: '10%',
        right: '15%',
        width: 400,
        height: 400,
        borderRadius: '50%',
        background: `radial-gradient(circle, ${alpha('#0066ff', 0.05)} 0%, transparent 70%)`,
        filter: 'blur(50px)',
        animation: 'orbFloat2 15s ease-in-out infinite',
        '@keyframes orbFloat2': {
          '0%, 100%': { transform: 'translate(0, 0)' },
          '50%': { transform: 'translate(-25px, -30px)' },
        },
      }}
    />

    {/* Scan line */}
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '2px',
        background: `linear-gradient(90deg, transparent 0%, ${alpha('#00d4ff', 0.15)} 50%, transparent 100%)`,
        animation: 'scanDown 8s ease-in-out infinite',
        '@keyframes scanDown': {
          '0%': { top: '-2px', opacity: 0 },
          '5%': { opacity: 1 },
          '95%': { opacity: 1 },
          '100%': { top: '100%', opacity: 0 },
        },
      }}
    />
  </Box>
);

// ==================== MARKET TICKER ====================

const MarketTicker = () => {
  const tickers = useMemo(
    () => [
      { symbol: 'NQ', value: '21,486.50', change: '+0.42%', up: true },
      { symbol: 'ES', value: '6,084.25', change: '+0.28%', up: true },
      { symbol: 'YM', value: '44,126.00', change: '-0.15%', up: false },
      { symbol: 'RTY', value: '2,287.40', change: '+0.67%', up: true },
      { symbol: 'GC', value: '2,948.30', change: '+0.12%', up: true },
      { symbol: 'CL', value: '71.84', change: '-0.34%', up: false },
    ],
    []
  );

  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: 36,
        bgcolor: alpha('#000', 0.5),
        borderTop: '1px solid',
        borderColor: alpha('#00d4ff', 0.08),
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        zIndex: 2,
        backdropFilter: 'blur(10px)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 4,
          animation: 'tickerScroll 30s linear infinite',
          whiteSpace: 'nowrap',
          '@keyframes tickerScroll': {
            '0%': { transform: 'translateX(0)' },
            '100%': { transform: 'translateX(-50%)' },
          },
        }}
      >
        {[...tickers, ...tickers].map((t, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              sx={{
                color: alpha('#fff', 0.4),
                fontSize: '0.7rem',
                fontFamily: '"JetBrains Mono", "SF Mono", "Fira Code", monospace',
                fontWeight: 600,
                letterSpacing: '0.05em',
              }}
            >
              {t.symbol}
            </Typography>
            <Typography
              sx={{
                color: alpha('#fff', 0.6),
                fontSize: '0.7rem',
                fontFamily: '"JetBrains Mono", "SF Mono", "Fira Code", monospace',
                fontWeight: 500,
              }}
            >
              {t.value}
            </Typography>
            <Typography
              sx={{
                color: t.up ? '#00e676' : '#ff5252',
                fontSize: '0.65rem',
                fontFamily: '"JetBrains Mono", "SF Mono", "Fira Code", monospace',
                fontWeight: 700,
              }}
            >
              {t.change}
            </Typography>
            <Box
              sx={{
                width: 3,
                height: 3,
                borderRadius: '50%',
                bgcolor: alpha('#fff', 0.1),
                mx: 1,
              }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ==================== MINI CHART SVG ====================

const MiniChart = ({ color = '#00d4ff', delay = 0 }) => {
  const points = useMemo(() => {
    const pts = [];
    let y = 50;
    for (let x = 0; x <= 100; x += 4) {
      y += (Math.random() - 0.48) * 12;
      y = Math.max(15, Math.min(85, y));
      pts.push(`${x},${y}`);
    }
    return pts.join(' ');
  }, []);

  return (
    <Box
      sx={{
        opacity: 0.12,
        animation: `fadeInChart 1.5s ease-out ${delay}s both`,
        '@keyframes fadeInChart': {
          from: { opacity: 0, transform: 'translateY(5px)' },
          to: { opacity: 0.12 },
        },
      }}
    >
      <svg width="100%" height="60" viewBox="0 0 100 100" preserveAspectRatio="none">
        <polyline
          points={points}
          fill="none"
          stroke={color}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </Box>
  );
};

// ==================== LOGIN COMPONENT ====================

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, login } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // ==================== REDIRECT IF ALREADY LOGGED IN ====================
  useEffect(() => {
    if (user) {
      const from = location.state?.from?.pathname;

      if (from && from !== '/login') {
        navigate(from, { replace: true });
      } else {
        if (user.role === 'admin') {
          navigate('/admin/strategies', { replace: true });
        } else if (user.role === 'officer') {
          navigate('/officer/bulk-strategies', { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      }
    }
  }, [user, navigate, location]);

  // ==================== HANDLERS ====================
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(formData.username, formData.password);

      if (result.success) {
        const userRole = result.user.role;
        const from = location.state?.from?.pathname;

        if (from && from !== '/login') {
          navigate(from, { replace: true });
        } else {
          if (userRole === 'admin') {
            navigate('/admin/strategies', { replace: true });
          } else if (userRole === 'officer') {
            navigate('/officer/nq-dashboard', { replace: true });
          } else {
            navigate('/dashboard', { replace: true });
          }
        }
      } else {
        setError(result.message || 'Invalid credentials. Please try again.');
      }
    } catch (err) {
      setError('An error occurred during login. Please try again.');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  // ==================== SHARED INPUT STYLES ====================
  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      bgcolor: alpha('#0c1220', 0.7),
      transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
      '& fieldset': {
        borderColor: alpha('#fff', 0.06),
        borderWidth: '1px',
        transition: 'all 0.25s',
      },
      '&:hover': {
        bgcolor: alpha('#0f1628', 0.9),
        '& fieldset': { borderColor: alpha('#00d4ff', 0.25) },
      },
      '&.Mui-focused': {
        bgcolor: alpha('#111b2e', 1),
        boxShadow: `0 0 0 3px ${alpha('#00d4ff', 0.1)}, inset 0 1px 2px ${alpha('#000', 0.2)}`,
        '& fieldset': { borderColor: alpha('#00d4ff', 0.6), borderWidth: '1px' },
      },
    },
    '& .MuiInputBase-input': {
      color: '#e8edf5',
      fontSize: '0.95rem',
      fontWeight: 500,
      py: 1.75,
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      '&::placeholder': { color: alpha('#8899b4', 0.5), opacity: 1 },
      '&:-webkit-autofill': {
        WebkitBoxShadow: '0 0 0 100px #111b2e inset',
        WebkitTextFillColor: '#e8edf5',
        caretColor: '#e8edf5',
        borderRadius: '12px',
      },
    },
  };

  // ==================== RENDER ====================
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
      {/* Google Fonts */}
      <link
        href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500;600;700&display=swap"
        rel="stylesheet"
      />

      <GridBackground />
      <MarketTicker />

      {/* Constrained inner wrapper — keeps both panels close together */}
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          maxWidth: 1440,
          position: 'relative',
          zIndex: 1,
        }}
      >

      {/* Left panel — branding */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flex: 1,
          flexDirection: 'column',
          justifyContent: 'center',
          px: { md: 6, lg: 10 },
          position: 'relative',
        }}
      >
        <Box sx={{ maxWidth: 480 }}>
          {/* Logo mark */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 5 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '10px',
                background: `linear-gradient(135deg, ${alpha('#00d4ff', 0.2)} 0%, ${alpha('#0066ff', 0.15)} 100%)`,
                border: '1px solid',
                borderColor: alpha('#00d4ff', 0.2),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ShowChart sx={{ color: '#00d4ff', fontSize: 24 }} />
            </Box>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontWeight: 800,
                fontSize: '1.15rem',
                letterSpacing: '-0.02em',
                color: '#fff',
              }}
            >
              TradeWave
            </Typography>
          </Box>

          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontWeight: 900,
              fontSize: { md: '2.4rem', lg: '3rem' },
              lineHeight: 1.1,
              letterSpacing: '-0.035em',
              color: '#fff',
              mb: 2.5,
            }}
          >
            Algorithmic
            <br />
            <Box
              component="span"
              sx={{
                background: 'linear-gradient(135deg, #00d4ff 0%, #0088ff 50%, #00d4ff 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundSize: '200% auto',
                animation: 'shimmer 3s linear infinite',
                '@keyframes shimmer': {
                  '0%': { backgroundPosition: '0% center' },
                  '100%': { backgroundPosition: '200% center' },
                },
              }}
            >
              Trading Terminal
            </Box>
          </Typography>

          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontSize: '1.05rem',
              lineHeight: 1.7,
              color: alpha('#94afd0', 0.7),
              fontWeight: 400,
              maxWidth: 400,
              mb: 5,
            }}
          >
            Real-time signal processing, multi-condition strategy execution, and automated position
            management for NQ futures.
          </Typography>

          {/* Feature pills */}
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            {[
              { icon: <TrendingUp sx={{ fontSize: 14 }} />, label: 'Multi-Timeframe' },
              { icon: <CandlestickChart sx={{ fontSize: 14 }} />, label: 'Live Signals' },
              { icon: <ShowChart sx={{ fontSize: 14 }} />, label: 'Auto Execution' },
            ].map((pill) => (
              <Box
                key={pill.label}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: '8px',
                  bgcolor: alpha('#00d4ff', 0.05),
                  border: '1px solid',
                  borderColor: alpha('#00d4ff', 0.1),
                  color: alpha('#00d4ff', 0.7),
                  fontSize: '0.75rem',
                  fontFamily: '"Inter", sans-serif',
                  fontWeight: 600,
                  letterSpacing: '0.02em',
                }}
              >
                {pill.icon}
                {pill.label}
              </Box>
            ))}
          </Stack>

          {/* Mini charts decoration */}
          <Box sx={{ mt: 6, maxWidth: 350 }}>
            <MiniChart color="#00d4ff" delay={0.2} />
            <MiniChart color="#0066ff" delay={0.5} />
          </Box>
        </Box>
      </Box>

      {/* Right panel — login form */}
      <Box
        sx={{
          flex: { xs: 1, md: '0 0 480px' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          px: { xs: 2, sm: 4 },
          py: 4,
          // Subtle left border separator on desktop
          borderLeft: { md: '1px solid' },
          borderColor: { md: alpha('#fff', 0.04) },
          bgcolor: { md: alpha('#070b12', 0.4) },
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 400 }}>
          {/* Mobile logo (only shows on small screens) */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1.5, mb: 4, justifyContent: 'center' }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                background: `linear-gradient(135deg, ${alpha('#00d4ff', 0.2)} 0%, ${alpha('#0066ff', 0.15)} 100%)`,
                border: '1px solid',
                borderColor: alpha('#00d4ff', 0.2),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ShowChart sx={{ color: '#00d4ff', fontSize: 22 }} />
            </Box>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontWeight: 800,
                fontSize: '1.1rem',
                letterSpacing: '-0.02em',
                color: '#fff',
              }}
            >
              TradeWave
            </Typography>
          </Box>

          {/* Form header */}
          <Box sx={{ mb: 4 }}>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontWeight: 800,
                fontSize: '1.7rem',
                letterSpacing: '-0.03em',
                color: '#fff',
                mb: 0.75,
              }}
            >
              Sign in
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: '0.9rem',
                color: alpha('#8899b4', 0.7),
                fontWeight: 400,
              }}
            >
              Enter your credentials to access the terminal
            </Typography>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                borderRadius: '12px',
                bgcolor: alpha('#ff3b30', 0.08),
                border: '1px solid',
                borderColor: alpha('#ff3b30', 0.2),
                color: '#ff6b6b',
                fontFamily: '"Inter", sans-serif',
                fontSize: '0.85rem',
                '& .MuiAlert-icon': { color: '#ff6b6b' },
              }}
            >
              {error}
            </Alert>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit}>
            <Stack spacing={2.5}>
              {/* Username */}
              <Box>
                <Typography
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    color: alpha('#8899b4', 0.8),
                    fontWeight: 600,
                    fontSize: '0.78rem',
                    letterSpacing: '0.04em',
                    mb: 1,
                  }}
                >
                  Username
                </Typography>
                <TextField
                  fullWidth
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  required
                  autoFocus
                  autoComplete="username"
                  placeholder="Enter your username"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person sx={{ color: alpha('#8899b4', 0.4), fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={inputSx}
                />
              </Box>

              {/* Password */}
              <Box>
                <Typography
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    color: alpha('#8899b4', 0.8),
                    fontWeight: 600,
                    fontSize: '0.78rem',
                    letterSpacing: '0.04em',
                    mb: 1,
                  }}
                >
                  Password
                </Typography>
                <TextField
                  fullWidth
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: alpha('#8899b4', 0.4), fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{
                            color: alpha('#8899b4', 0.4),
                            '&:hover': { color: '#00d4ff', bgcolor: alpha('#00d4ff', 0.08) },
                          }}
                        >
                          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={inputSx}
                />
              </Box>

              {/* Sign In Button */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                endIcon={loading ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <ArrowForward sx={{ fontSize: 18 }} />}
                sx={{
                  py: 1.75,
                  mt: 1,
                  borderRadius: '12px',
                  fontFamily: '"Inter", sans-serif',
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  textTransform: 'none',
                  letterSpacing: '-0.01em',
                  background: 'linear-gradient(135deg, #00b8e6 0%, #0077cc 100%)',
                  boxShadow: `0 4px 20px ${alpha('#00b8e6', 0.3)}, 0 1px 3px ${alpha('#000', 0.2)}`,
                  border: 'none',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(135deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%)',
                    opacity: 0,
                    transition: 'opacity 0.3s',
                  },
                  '&:hover': {
                    background: 'linear-gradient(135deg, #00c8f0 0%, #0088dd 100%)',
                    boxShadow: `0 6px 28px ${alpha('#00b8e6', 0.4)}, 0 2px 6px ${alpha('#000', 0.15)}`,
                    transform: 'translateY(-1px)',
                    '&::after': { opacity: 1 },
                  },
                  '&:active': { transform: 'translateY(0)' },
                  '&:disabled': {
                    background: alpha('#1e2a3a', 0.6),
                    color: alpha('#8899b4', 0.4),
                    boxShadow: 'none',
                  },
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                {loading ? 'Authenticating...' : 'Sign In'}
              </Button>
            </Stack>
          </form>

          {/* Divider */}
          <Box sx={{ my: 3.5, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ flex: 1, height: '1px', bgcolor: alpha('#fff', 0.05) }} />
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                color: alpha('#8899b4', 0.3),
                fontWeight: 500,
                fontSize: '0.65rem',
                letterSpacing: '0.15em',
              }}
            >
              OR
            </Typography>
            <Box sx={{ flex: 1, height: '1px', bgcolor: alpha('#fff', 0.05) }} />
          </Box>

          {/* Signup link */}
          <Box
            sx={{
              p: 2.5,
              borderRadius: '12px',
              bgcolor: alpha('#fff', 0.02),
              border: '1px solid',
              borderColor: alpha('#fff', 0.05),
              textAlign: 'center',
              transition: 'all 0.2s',
              '&:hover': {
                bgcolor: alpha('#00d4ff', 0.03),
                borderColor: alpha('#00d4ff', 0.12),
              },
            }}
          >
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                color: alpha('#8899b4', 0.5),
                fontSize: '0.85rem',
                mb: 1,
              }}
            >
              Don't have an account?
            </Typography>
            <Link
              to="/admin-signup"
              style={{
                color: '#00d4ff',
                textDecoration: 'none',
                fontFamily: '"Inter", sans-serif',
                fontWeight: 700,
                fontSize: '0.9rem',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s',
              }}
            >
              Create Admin Account
              <ArrowForward style={{ fontSize: 16 }} />
            </Link>
          </Box>

          {/* Officer note */}
          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontSize: '0.75rem',
              color: alpha('#8899b4', 0.3),
              textAlign: 'center',
              mt: 2,
            }}
          >
            Officers: Contact your administrator to create your account
          </Typography>

          {/* Footer */}
          <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid', borderColor: alpha('#fff', 0.04), textAlign: 'center' }}>
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.68rem',
                color: alpha('#8899b4', 0.25),
                letterSpacing: '0.06em',
              }}
            >
              TradeWave Terminal v2.1.0
            </Typography>
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.6rem',
                color: alpha('#00d4ff', 0.2),
                mt: 0.5,
                letterSpacing: '0.08em',
              }}
            >
              Secure Algorithmic Trading Platform
            </Typography>
          </Box>
        </Box>
      </Box>

      </Box>{/* End constrained inner wrapper */}
    </Box>
  );
};

export default Login;
